import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import GridTableSkeleton from "./GridTableSkeleton";

export type TableHeader = {
  key: string;
  title: React.ReactNode;
  headerClassName?: string;
  cellClassName?: string;
  enableSorting?: boolean;
  enablePinning?: boolean;
  pin?: "left" | "right";
};

export type ColumnMeta = {
  headerClassName?: string;
  cellClassName?: string;
  pin?: "left" | "right";
};

type GridTableProps<T> = {
  headers?: TableHeader[];
  columns?: ColumnDef<T, any>[];
  rows: T[];
  className?: string;
  pageSize?: number;
  isLoading?: boolean;
  isEmpty?: boolean;
  totalCount?: number;
  hidePagination?: boolean;
};

const GridTable = <T extends object>({
  isLoading = false,
  headers,
  columns,
  rows,
  className = "",
  pageSize = 10,
  totalCount,
  hidePagination = false,
}: GridTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columnDefs: ColumnDef<T, any>[] = useMemo(() => {
    if (columns && columns.length > 0) {
      return columns;
    }
    if (!headers) {
      return [];
    }
    return headers.map((header) => ({
      accessorKey: header.key,
      header: () => header.title,
      enableSorting: header.enableSorting ?? true,
      enablePinning: header.enablePinning ?? true,
      cell: (context: any) => context.renderValue(),
      meta: {
        headerClassName: header.headerClassName,
        cellClassName: header.cellClassName,
        pin: header.pin,
      } as ColumnMeta,
    }));
  }, [columns, headers]);

  const pinnedConfig = useMemo<ColumnPinningState>(() => {
    const left: string[] = [];
    const right: string[] = [];
    columnDefs.forEach((column) => {
      const meta = (column.meta || {}) as ColumnMeta;
      const columnId =
        column.id ??
        (typeof (column as any).accessorKey === "string"
          ? ((column as any).accessorKey as string)
          : undefined);
      if (!columnId) {
        return;
      }
      if (meta.pin === "left") {
        left.push(columnId);
      }
      if (meta.pin === "right") {
        right.push(columnId);
      }
    });
    return { left, right };
  }, [columnDefs]);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(pinnedConfig);

  useEffect(() => {
    setColumnPinning(pinnedConfig);
  }, [pinnedConfig]);

  const table = useReactTable<T>({
    data: rows,
    columns: columnDefs,
    state: { sorting, columnPinning },
    onSortingChange: setSorting,
    onColumnPinningChange: setColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize } },
  });

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSizeState = table.getState().pagination.pageSize;
  const total = typeof totalCount === "number" ? totalCount : rows.length;
  const shown = Math.min((pageIndex + 1) * pageSizeState, total);

  useEffect(() => {
    if (hidePagination) {
      table.setPageIndex(0);
      table.setPageSize(rows.length || pageSize);
    } else {
      table.setPageSize(pageSize);
    }
  }, [hidePagination, rows.length, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolvePinnedStyle = (
    pinned: "left" | "right" | false,
    start: number,
    after: number,
    baseZIndex: number
  ): CSSProperties | undefined => {
    if (pinned === "left") {
      return {
        position: "sticky",
        left: `${start}px`,
        zIndex: baseZIndex,
        background: "#ffffff",
        overflow: "visible",
      };
    }
    if (pinned === "right") {
      return {
        position: "sticky",
        right: `${after}px`,
        zIndex: baseZIndex,
        background: "#ffffff",
        overflow: "visible",
      };
    }
    return undefined;
  };

  if (isLoading) {
    return (
      <>
        <GridTableSkeleton rowCount={pageSize} columnCount={columnDefs.length} />
        {!hidePagination && (
          <div className="flex items-center justify-between pt-3">
            <div className="text-gray-500 text-sm">Loading...</div>
            <div className="flex items-center gap-3">
              <button
                className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                disabled={true}
                title="Prev"
              >
                ‹
              </button>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="inline-flex items-center justify-center w-12 h-9 rounded-lg border border-gray-200 bg-white">
                  -
                </span>
                <span>of -</span>
              </div>
              <button
                className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                disabled={true}
                title="Next"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div className={`border border-gray-200 rounded-lg overflow-hidden bg-white ${className}`}>
        <div className="overflow-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-gray-200">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortedState = header.column.getIsSorted();
                    const meta = (header.column.columnDef.meta || {}) as ColumnMeta;
                    const style = resolvePinnedStyle(
                      header.column.getIsPinned(),
                      header.column.getStart("left"),
                      header.column.getAfter("right"),
                      30
                    );

                    return (
                      <th
                        key={header.id}
                        className={`px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider h-10 ${
                          canSort ? "cursor-pointer" : ""
                        } ${meta.headerClassName || ""}`}
                        style={style}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        title={
                          canSort
                            ? sortedState === "asc"
                              ? "Sorted asc"
                              : sortedState === "desc"
                                ? "Sorted desc"
                                : "Click to sort"
                            : undefined
                        }
                      >
                        <div className="flex items-center space-x-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sortedState === "asc" && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                          {sortedState === "desc" && (
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 h-11">
                  {row.getVisibleCells().map((cell) => {
                    const meta = (cell.column.columnDef.meta || {}) as ColumnMeta;
                    const style = resolvePinnedStyle(
                      cell.column.getIsPinned(),
                      cell.column.getStart("left"),
                      cell.column.getAfter("right"),
                      20
                    );

                    return (
                      <td
                        key={cell.id}
                        className={`px-5 py-3 font-medium text-gray-700 ${
                          meta.cellClassName || ""
                        }`}
                        style={style}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {!hidePagination && (
        <div className="flex items-center justify-between pt-3">
          <div className="text-gray-500 text-sm">
            {shown} of {total} results
          </div>
          <div className="flex items-center gap-3">
            <button
              className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              title="Prev"
            >
              ‹
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="inline-flex items-center justify-center w-12 h-9 rounded-lg border border-gray-200 bg-white">
                {table.getState().pagination.pageIndex + 1}
              </span>
              <span>of {table.getPageCount() || 1}</span>
            </div>
            <button
              className="w-9 h-9 rounded-lg border border-gray-200 hover:bg-gray-50"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              title="Next"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GridTable;
